import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { selectIsAdmin, selectIsApproved } from '../store/userSlice';
import { toast } from 'react-toastify';
import './Navbar.css';

function Navbar() {
  const { isAuthenticated, logout, hasRole, userType, username, name, sportsClubName, teamName, teams, selectedTeamId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useNotifications();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Zamknij menu po zmianie strony
  useEffect(() => {
    setMoreMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setMoreMenuOpen(false);
    await logout();
    navigate('/login');
  };

  // Sprawdź role użytkownika
  const isCoach = hasRole('COACH');
  const isSportsClub = hasRole('SPORTS_CLUB');
  const isAdmin = useSelector(selectIsAdmin);
  const isApproved = useSelector(selectIsApproved);

  const isResultsPage = location.pathname === '/results';
  const showApprovalBanner = isAuthenticated() && isSportsClub && isApproved === false;

  // Coach bez zdefiniowanych okresów przygotowawczych — blokuj nawigację
  const needsPreseasonSetup = isCoach && teams?.length > 0 && !teams.some(t => t.isPreseasonDefined);

  const handleBlockedNavClick = (e) => {
    if (needsPreseasonSetup) {
      e.preventDefault();
      toast.error('Aby rozpocząć korzystanie z aplikacji musisz zdefiniować okresy przygotowawcze');
    }
  };

  return (
    <>
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-logo">
          <div className="navbar-logo-stacked">
            <img src="/favicon_newest.png" alt="Sparingownia" className="navbar-logo-img navbar-logo-favicon" />
            <span className="navbar-logo-text">Sparingownia</span>
          </div>
        </div>

        {/* Desktop nav links - hidden on mobile */}
        <ul className="navbar-menu">
          {isAuthenticated() && !isResultsPage && (
            <>
              {isAdmin ? (
                <li>
                  <NavLink to="/admin/access" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Panel administracyjny
                  </NavLink>
                </li>
              ) : (
                <>
                  {isCoach && (
                    <>
                      <li>
                        <NavLink to="/preseason-games-plans" onClick={handleBlockedNavClick} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Twoje plany
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/sports-club-matches" onClick={handleBlockedNavClick} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Twoje zapytania
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/sports-club-received-matches" onClick={handleBlockedNavClick} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Otrzymane zapytania
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/preseason-games-summary" onClick={handleBlockedNavClick} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Podsumowanie
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/management" onClick={handleBlockedNavClick} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Zarządzanie
                        </NavLink>
                      </li>
                    </>
                  )}

                  {isSportsClub && (
                    <>
                      <li>
                        <NavLink to="/preseason-games-plans" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Twoje plany
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/sports-club-matches" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Twoje zapytania
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/sports-club-received-matches" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Otrzymane zapytania
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/preseason-games-summary" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Podsumowanie
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/management" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                          Zarządzanie
                        </NavLink>
                      </li>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </ul>

        {/* Auth section */}
        <div className="navbar-auth">
          {isAuthenticated() && !isResultsPage ? (
            <>
              <NavLink to="/account" onClick={handleBlockedNavClick} className={({ isActive }) => isActive ? 'account-button active' : 'account-button'}>
                {isSportsClub && name && <span className="account-username">{name}</span>}
                {!isSportsClub && username && <span className="account-username">{username}</span>}
              </NavLink>
              <NavLink to="/notifications" onClick={handleBlockedNavClick} className="notification-icon-link">
                <span className="notification-icon">🔔</span>
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </NavLink>
              <button onClick={handleLogout} className="logout-link">
                Wyloguj
              </button>
            </>
          ) : null}
        </div>
      </div>

      {/* Mobile bottom navigation bar */}
      {isAuthenticated() && !isResultsPage && (
        <>
          <div className="mobile-bottom-bar">
            <NavLink
              to="/preseason-games-plans"
              onClick={handleBlockedNavClick}
              className={({ isActive }) => 'bottom-bar-item' + (isActive ? ' bottom-bar-item--active' : '')}
            >
              <svg className="bottom-bar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span className="bottom-bar-label">Plany</span>
            </NavLink>
            <NavLink
              to="/sports-club-matches"
              onClick={handleBlockedNavClick}
              className={({ isActive }) => 'bottom-bar-item' + (isActive ? ' bottom-bar-item--active' : '')}
            >
              <svg className="bottom-bar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              <span className="bottom-bar-label">Wysłane</span>
            </NavLink>
            <NavLink
              to="/sports-club-received-matches"
              onClick={handleBlockedNavClick}
              className={({ isActive }) => 'bottom-bar-item' + (isActive ? ' bottom-bar-item--active' : '')}
            >
              <svg className="bottom-bar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
              </svg>
              <span className="bottom-bar-label">Otrzymane</span>
            </NavLink>
            <button
              className={'bottom-bar-item bottom-bar-more-btn' + (moreMenuOpen ? ' bottom-bar-item--active' : '')}
              onClick={() => setMoreMenuOpen(prev => !prev)}
            >
              <svg className="bottom-bar-icon" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="19" r="2" />
              </svg>
              <span className="bottom-bar-label">Więcej</span>
            </button>
          </div>

          {/* Mobile more menu */}
          {moreMenuOpen && (
            <div className="mobile-more-overlay" onClick={() => setMoreMenuOpen(false)}>
              <div className="mobile-more-menu" onClick={e => e.stopPropagation()}>
                <div className="more-menu-header">
                  <span>Menu</span>
                  <button className="more-menu-close" onClick={() => setMoreMenuOpen(false)}>✕</button>
                </div>
                {isAdmin ? (
                  <NavLink to="/admin/access" className="more-menu-link" onClick={() => setMoreMenuOpen(false)}>
                    <span className="more-menu-icon">🔑</span>
                    Panel administracyjny
                  </NavLink>
                ) : (
                  <>
                    {(isSportsClub || isCoach) && (
                      <NavLink to="/preseason-games-summary" className="more-menu-link" onClick={(e) => { if (isCoach && needsPreseasonSetup) { e.preventDefault(); toast.error('Aby rozpocząć korzystanie z aplikacji musisz zdefiniować okresy przygotowawcze'); } else { setMoreMenuOpen(false); } }}>
                        <span className="more-menu-icon">📊</span>
                        Podsumowanie
                      </NavLink>
                    )}
                    <NavLink to="/management" className="more-menu-link" onClick={(e) => { if (needsPreseasonSetup) { e.preventDefault(); toast.error('Aby rozpocząć korzystanie z aplikacji musisz zdefiniować okresy przygotowawcze'); } else { setMoreMenuOpen(false); } }}>
                      <span className="more-menu-icon">⚙️</span>
                      Zarządzanie
                    </NavLink>
                    <NavLink to="/account" className="more-menu-link" onClick={() => setMoreMenuOpen(false)}>
                      <span className="more-menu-icon">👤</span>
                      Konto
                    </NavLink>
                  </>
                )}
                <div className="more-menu-divider" />
                <NavLink to="/regulamin" className="more-menu-link" onClick={() => setMoreMenuOpen(false)}>
                  <span className="more-menu-icon">📄</span>
                  Regulamin
                </NavLink>
                <NavLink to="/polityka-prywatnosci" className="more-menu-link" onClick={() => setMoreMenuOpen(false)}>
                  <span className="more-menu-icon">🔒</span>
                  Polityka prywatności
                </NavLink>
                <div className="more-menu-divider" />
                <button className="more-menu-link more-menu-logout" onClick={handleLogout}>
                  <span className="more-menu-icon">🚪</span>
                  Wyloguj
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </nav>
      {showApprovalBanner && (
        <div className="approval-banner">
          Twoje konto oczekuje na weryfikację. Możesz przeglądać i dodawać terminy, ale składanie i akceptacja zapytań wymaga weryfikacji. Wykonaj kroki opisane w mailu weryfikacyjnym otrzymanym przy rejestracji.
        </div>
      )}
    </>
  );
}

export default Navbar;
